import { useState } from "react"
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/Button"
import { Unplug, CheckCircle, Plus, Clock } from "lucide-react"
import { cn } from "@/utils/Functions"
import type { NylasConnection } from "@equaliq/api-model"
import type { Integration } from "@/types/integrations.types"
import { scanMoreOnboardingApi } from "@/api/integrations"
import { toast } from "sonner"

interface ManageConnectionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: Integration
  connections: NylasConnection[]
  onDisconnect: (connectionId: string) => void
  onAddAnother: () => void
  disconnecting?: boolean
  connecting?: boolean
}

export function ManageConnectionsDialog({
  open,
  onOpenChange,
  integration,
  connections,
  onDisconnect,
  onAddAnother,
  disconnecting = false,
  connecting = false,
}: ManageConnectionsDialogProps) {
  const Icon = integration.icon
  // #205-D — pending-confirm state so the destructive disconnect doesn't
  // fire on the first click. AlertDialog opens, user explicitly confirms,
  // THEN onDisconnect runs.
  const [pendingDisconnect, setPendingDisconnect] = useState<NylasConnection | null>(null)
  const [scanning, setScanning] = useState(false)

  const confirmDisconnect = () => {
    if (!pendingDisconnect) return
    onDisconnect(pendingDisconnect.connectionId)
    setPendingDisconnect(null)
  }

  // Frontend tracks the scan cursor in localStorage so successive
  // "scan more" clicks extend further back instead of re-scanning the
  // same window. Backend is stateless on the cursor — it just fires the
  // workflow with whatever `untilEpochSeconds` we send. When unset (first
  // click after the original 12-month onboard), backend defaults to
  // (now - 365d).
  const scanCursorKey = `eiq:scan-more-cursor:${integration.id}`
  const handleScanMore = async () => {
    if (scanning) return
    setScanning(true)
    try {
      const stored = localStorage.getItem(scanCursorKey)
      const untilEpochSeconds = stored ? parseInt(stored, 10) : undefined
      const result = await scanMoreOnboardingApi({
        channel: 'email',
        additionalWindows: 3,
        ...(untilEpochSeconds && !Number.isNaN(untilEpochSeconds)
          ? { untilEpochSeconds }
          : {}),
      })
      if (result.workflowId && result.scannedFromEpochSeconds) {
        // Persist the new floor so the next click extends past this scan.
        localStorage.setItem(scanCursorKey, String(result.scannedFromEpochSeconds))
        toast.success('Scanning 3 more months of inbox history', {
          description: result.reason,
        })
      } else {
        toast.error("Couldn't start scan", { description: result.reason })
      }
    } catch (err) {
      toast.error('Scan request failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setScanning(false)
    }
  }
  // Channels with bulk-history APIs that the backend onboard workflow
  // can paginate. Currently Gmail (via Nylas v3) is the only one with a
  // working backfill path; outlook will work the same way once we have a
  // Nylas grant. iMessage providers (sendblue, linq) have no history API
  // and are intentionally excluded.
  const supportsHistoryScan = ['gmail', 'outlook', 'email'].includes(integration.id)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPopup>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div
                className={cn(
                  "flex items-center justify-center size-10 rounded-xl bg-gradient-to-br border",
                  integration.accentFrom,
                  integration.accentTo,
                  integration.accentBorder
                )}
              >
                <Icon className="size-5 text-foreground/80" />
              </div>
              <DialogTitle>Manage {integration.name}</DialogTitle>
            </div>
            <DialogDescription>
              {connections.length === 1
                ? "You have 1 connected account"
                : `You have ${connections.length} connected accounts`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-4">
            {connections.map((conn) => (
              <div
                key={conn.connectionId}
                className="flex items-center justify-between p-4 rounded-lg border border-border bg-card/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-foreground truncate">
                      {conn.email || `${conn.provider} account`}
                    </p>
                    {conn.enabled && (
                      <CheckCircle className="size-4 text-green-400 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Provider: {conn.provider} • Connected{" "}
                    {new Date(conn.connectedAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-4 gap-2 text-destructive hover:bg-destructive/10 shrink-0"
                  onClick={() => setPendingDisconnect(conn)}
                  disabled={disconnecting}
                >
                  <Unplug className="size-3.5" />
                  Disconnect
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-border space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={onAddAnother}
              disabled={connecting}
            >
              <Plus className="size-4" />
              Add another account
            </Button>
            {supportsHistoryScan && connections.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full gap-2 text-muted-foreground hover:text-foreground"
                onClick={handleScanMore}
                disabled={scanning}
              >
                <Clock className="size-4" />
                {scanning ? 'Starting scan…' : 'Scan more inbox history (3 months)'}
              </Button>
            )}
          </div>
        </DialogPopup>
      </Dialog>

      <AlertDialog
        open={!!pendingDisconnect}
        onOpenChange={(open) => {
          if (!open && !disconnecting) setPendingDisconnect(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {integration.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDisconnect
                ? `${pendingDisconnect.email || `Your ${pendingDisconnect.provider} account`} will be disconnected from Equal-IQ. You can reconnect anytime, but in-flight email/calendar sync will stop.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={disconnecting}
              onClick={() => setPendingDisconnect(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={disconnecting}
              onClick={confirmDisconnect}
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
